import { useEffect, useState, useCallback } from 'react';
import { clsx } from 'clsx';
import { Input } from '../Input';
import { Select } from '../Select';
import { Textarea } from '../Textarea/Textarea';
import { InputNumber } from '../InputNumber/InputNumber';
import type { LineActionField } from './template/MasterDocumentTemplate.types';
import { useMasterDocumentTranslation } from './i18n';

// ─── Icons ─────────────────────────────────────────────────

function XIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" {...props}>
      <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
    </svg>
  );
}

// ─── Props ─────────────────────────────────────────────────

export interface LineActionFormModalProps {
  open: boolean;
  onClose: () => void;
  /** Título del modal (de `LineActionEntry.formModal.title`). */
  title: string;
  /** Etiqueta del botón de confirmación. Por defecto "Confirmar". */
  submitLabel?: string;
  /** Campos declarativos a renderizar. */
  fields: LineActionField[];
  /** Se invoca con los valores del formulario al confirmar (tras validar `required`). */
  onSubmit: (formData: Record<string, unknown>) => void;
  /** Deshabilita los controles y el submit mientras la acción está en curso. */
  busy?: boolean;
  /** Mensaje de error a nivel de modal (p.ej. fallo del endpoint). */
  error?: string;
}

// ─── Helpers ───────────────────────────────────────────────

function isEmpty(value: unknown): boolean {
  return value == null || value === '';
}

// ─── LineActionFormModal ───────────────────────────────────

/**
 * Modal de formulario genérico para acciones de línea ADICIONALES declarativas
 * (`LineActionEntry.actionType === 'formModal'`), p.ej. "cerrar línea" (tipo de
 * cierre + notas).
 *
 * Renderiza los `fields` declarados reutilizando los primitivos de form del kit
 * (`Select`, `Textarea`, `Input`, `InputNumber`), valida los campos `required` y
 * emite `onSubmit(formData)` con los valores capturados. No conoce el endpoint ni
 * el adapter: solo recolecta y valida. El estado del formulario se reinicia cada
 * vez que el modal se abre.
 *
 * @see LineActionField, LineActionEntry
 */
export function LineActionFormModal({
  open,
  onClose,
  title,
  submitLabel,
  fields,
  onSubmit,
  busy = false,
  error,
}: LineActionFormModalProps) {
  const { t } = useMasterDocumentTranslation();
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Reinicia el formulario cada vez que el modal se abre.
  useEffect(() => {
    if (open) {
      setValues({});
      setErrors({});
    }
  }, [open]);

  // Cierra con Escape.
  useEffect(() => {
    if (!open) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  const setField = useCallback((name: string, value: unknown) => {
    setValues((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  }, []);

  const handleSubmit = useCallback(() => {
    const nextErrors: Record<string, string> = {};
    for (const field of fields) {
      if (field.required && isEmpty(values[field.name])) {
        nextErrors[field.name] = t('master_document.field.required');
      }
    }
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }
    onSubmit(values);
  }, [fields, values, onSubmit, t]);

  if (!open) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 z-[300] bg-black/30 animate-[fadeIn_150ms_ease-out]"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        className={clsx(
          'fixed z-[301] bg-bg-primary rounded-2xl overflow-hidden shadow-lg',
          'animate-[fadeIn_150ms_ease-out]',
          'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
          'w-[calc(100%-32px)] max-w-[520px] max-h-[85vh]',
          'max-sm:top-auto max-sm:bottom-0 max-sm:left-0 max-sm:right-0 max-sm:translate-x-0 max-sm:translate-y-0',
          'max-sm:w-full max-sm:max-w-none max-sm:rounded-b-none max-sm:max-h-[90vh]',
          'dark:bg-dark-bg-primary dark:shadow-none dark:border dark:border-dark-border-primary',
        )}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border-secondary dark:border-dark-border-primary">
          <h2 className="text-base font-bold text-content-primary dark:text-dark-content-primary">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-md hover:bg-background-secondary text-content-secondary transition-all duration-150 focus:outline-hidden focus:ring-2 focus:ring-primary-400 focus:ring-offset-2 dark:focus:ring-dark-border-custom dark:focus:ring-offset-dark-bg-primary dark:hover:bg-dark-bg-secondary"
            aria-label={t('master_document.chrome.closeDialog')}
          >
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto px-6 py-5 flex flex-col gap-4" style={{ maxHeight: 'calc(85vh - 130px)' }}>
          {error && (
            <div
              role="alert"
              className="rounded-md bg-error-bg px-3 py-2 text-sm text-error-content dark:bg-error-content/10"
            >
              {error}
            </div>
          )}

          {fields.map((field) => {
            const fieldError = errors[field.name];
            const commonId = `line-action-field-${field.name}`;

            if (field.type === 'select') {
              return (
                <Select
                  key={field.name}
                  id={commonId}
                  name={field.name}
                  label={field.label}
                  showLabel
                  required={field.required}
                  placeholder={field.placeholder ?? t('master_document.field.selectPlaceholder')}
                  options={(field.options ?? []).map((o) => ({ value: o.value, label: o.label }))}
                  value={(values[field.name] as string | number | undefined) ?? ''}
                  onChange={(value) => setField(field.name, value)}
                  disabled={busy}
                  error={fieldError ? true : undefined}
                  errorMessage={fieldError}
                />
              );
            }

            if (field.type === 'textarea') {
              return (
                <Textarea
                  key={field.name}
                  id={commonId}
                  name={field.name}
                  label={field.label}
                  required={field.required}
                  placeholder={field.placeholder}
                  value={(values[field.name] as string | undefined) ?? ''}
                  onChange={(e) => setField(field.name, e.target.value)}
                  disabled={busy}
                  error={fieldError ? true : undefined}
                  errorMessage={fieldError}
                />
              );
            }

            if (field.type === 'number') {
              return (
                <InputNumber
                  key={field.name}
                  id={commonId}
                  name={field.name}
                  label={field.label}
                  required={field.required}
                  placeholder={field.placeholder}
                  value={(values[field.name] as string | number | undefined) ?? ''}
                  onChange={(e) => setField(field.name, e.target.value)}
                  disabled={busy}
                  error={fieldError ? true : undefined}
                  errorMessage={fieldError}
                />
              );
            }

            // 'text'
            return (
              <Input
                key={field.name}
                id={commonId}
                name={field.name}
                label={field.label}
                required={field.required}
                placeholder={field.placeholder}
                value={(values[field.name] as string | undefined) ?? ''}
                onChange={(e) => setField(field.name, e.target.value)}
                disabled={busy}
                error={fieldError ? true : undefined}
                errorMessage={fieldError}
              />
            );
          })}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border-secondary dark:border-dark-border-primary">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className={clsx(
              // Secundario del canon: LLEVA borde (ver la nota en `LineFormModal`).
              'px-4 py-2 rounded-md text-sm font-semibold',
              'border border-border-primary dark:border-dark-border-primary',
              'text-content-tertiary hover:bg-background-secondary transition-all duration-150',
              'focus:outline-hidden focus:ring-2 focus:ring-primary-400 focus:ring-offset-2',
              'dark:focus:ring-dark-border-custom dark:focus:ring-offset-dark-bg-primary',
              'dark:text-dark-content-secondary dark:hover:bg-dark-bg-secondary',
              'disabled:opacity-50 disabled:cursor-not-allowed',
            )}
          >
            {t('master_document.chrome.cancel')}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={busy}
            className={clsx(
              'px-4 py-2 rounded-md text-sm font-semibold',
              'bg-primary-600 text-white hover:bg-primary-700 transition-all duration-150',
              'focus:outline-hidden focus:ring-2 focus:ring-primary-400 focus:ring-offset-2',
              'dark:focus:ring-dark-border-custom dark:focus:ring-offset-dark-bg-primary',
              'disabled:opacity-50 disabled:cursor-not-allowed',
            )}
          >
            {submitLabel ?? t('master_document.chrome.confirm')}
          </button>
        </div>
      </div>
    </>
  );
}
